import {
  TextField,
  useTranslate,
  FunctionField,
  BooleanField,
  useNotify
} from "react-admin";
import CustomList from "./CustomList";
import CustomDataGrid from "./CustomDataGrid";
import ListDotsActions from "./ListDotsActions";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemButton,
  TextField as MuiTextField,
  Theme,
  useMediaQuery
} from "@mui/material";
import { CustomButton } from "./CustomButton";
import { useState } from "react";
import dayjs from "dayjs";
import { useAxios } from "providers/axios";
import { DownloadIcon } from "assets/Icons";

interface CompanyExportResponse {
  columns: string[];
  data: Record<string, string | number | null>[];
}

const escapeCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";

  const stringValue =
    typeof value === "string" && /^[=+\-@]/.test(value)
      ? `'${value}`
      : String(value);

  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.URL.revokeObjectURL(url);
};

function CompanyExportButton() {
  const translate = useTranslate();
  const notify = useNotify();
  const { api } = useAxios();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [createdFrom, setCreatedFrom] = useState<string>("");
  const [createdTo, setCreatedTo] = useState<string>("");

  const isDateRangeInvalid = Boolean(
    createdFrom && createdTo && dayjs(createdFrom).isAfter(dayjs(createdTo))
  );

  const handleClose = () => {
    if (!isExporting) {
      setIsOpen(false);
    }
  };

  const handleClearFilters = () => {
    setCreatedFrom("");
    setCreatedTo("");
  };

  const handleExport = async () => {
    if (isDateRangeInvalid) return;

    const filters: {
      createdFrom?: string;
      createdTo?: string;
    } = {};

    if (createdFrom) {
      filters.createdFrom = dayjs(createdFrom).startOf("day").toISOString();
    }

    if (createdTo) {
      filters.createdTo = dayjs(createdTo).endOf("day").toISOString();
    }

    try {
      setIsExporting(true);

      const response = await api.post<CompanyExportResponse>(
        "/v1/companies/export",
        filters
      );
      const { columns, data } = response.data;
      const csv = [
        columns.map(escapeCsvValue).join(","),
        ...data.map((row) =>
          columns.map((column) => escapeCsvValue(row[column])).join(",")
        )
      ].join("\n");

      downloadCsv(`companies_${dayjs().format("YYYY-MM-DD_HH-mm")}.csv`, csv);
      setIsOpen(false);
    } catch (error) {
      notify(translate("common.something_went_wrong"), { type: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <CustomButton
        variant="secondary"
        text={translate("common.export_to_csv")}
        startIcon={<DownloadIcon />}
        onClick={() => setIsOpen(true)}
      />
      <Dialog
        open={isOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "#FAF9F7",
            borderRadius: "8px"
          }
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "OptimaLTStd",
            fontSize: "22px",
            fontWeight: 600
          }}
        >
          {translate("companies.export_companies")}
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-[16px] pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
              <MuiTextField
                label={translate("companies.created_from")}
                type="date"
                value={createdFrom}
                onChange={(event) => setCreatedFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: createdTo || undefined }}
                fullWidth
                size="small"
              />
              <MuiTextField
                label={translate("companies.created_to")}
                type="date"
                value={createdTo}
                onChange={(event) => setCreatedTo(event.target.value)}
                error={isDateRangeInvalid}
                helperText={
                  isDateRangeInvalid
                    ? translate("companies.created_date_range_invalid")
                    : undefined
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: createdFrom || undefined }}
                fullWidth
                size="small"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "space-between",
            padding: "16px 24px 24px"
          }}
        >
          <CustomButton
            variant="tertiary"
            text={translate("common.clear_filters")}
            onClick={handleClearFilters}
            disabled={!createdFrom && !createdTo}
          />
          <div className="flex items-center gap-[8px]">
            <CustomButton
              variant="tertiary"
              text={translate("common.cancel")}
              onClick={handleClose}
              disabled={isExporting}
            />
            <CustomButton
              text={translate("common.export_to_csv")}
              onClick={handleExport}
              submitting={isExporting}
              disabled={isExporting || isDateRangeInvalid}
            />
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function CompanyList() {
  const translate = useTranslate();
  const isSmall = useMediaQuery<Theme>("(max-width:800px)");

  return (
    <CustomList
      title={translate("pagenames.companies")}
      hasCreate={false}
      exporter={false}
      customExport={<CompanyExportButton />}
    >
      <CustomDataGrid bulkActionButtons={false}>
        <TextField
          source="name"
          label={translate("companies.name")}
          data-label={translate("companies.name") + ":"}
          sx={{ marginTop: "6px" }}
        />
        <BooleanField
          source="is_verified"
          label={translate("companies.is_verified")}
          data-label={translate("companies.is_verified") + ":"}
          sx={{ marginTop: "6px" }}
        />
        {!isSmall && (
          <FunctionField
            render={(record: any) => (
              <ListDotsActions>
                <Link to={`/dashboard/Company/${record?.id}`}>
                  <ListItemButton
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Inter",
                      "& .MuiListItemButton-root": {
                        fontSize: "14px",
                        fontFamily: "Inter"
                      }
                    }}
                  >
                    {translate("common.edit")}
                  </ListItemButton>
                </Link>
              </ListDotsActions>
            )}
          />
        )}
        {isSmall && (
          <FunctionField
            render={(record: any) => (
              <Link
                to={`/dashboard/Company/${record?.id}/show`}
                style={{ width: "100%" }}
              >
                <CustomButton
                  variant="secondary"
                  text={translate("common.view_details")}
                  sx={{ width: "100%", marginBottom: "10px" }}
                />
              </Link>
            )}
          />
        )}
      </CustomDataGrid>
    </CustomList>
  );
}
